import { type StepModel } from "../../../models/basics/basics.models";
import { ViewPeople } from "./view-people";
import { ZeroharmPartner } from "./zeroharm-partner/zeroharm-partner";
import { autoinject, observable, computedFrom } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { CustomerService } from "../../../services/modules/customer-client.service";
import { CustomerModel } from "../../../models/customers/customer.models";
import { BusinessUnit } from "../modules/customers/steps/business-units/business-units";
import { Claims } from "../../../constants/authentication/claims";
import { CustomerRoles } from "../modules/customers/steps/roles/roles";
import { Reflection } from "../../../utilities/reflection";
import { LookupValue, LookupValueRequest } from "../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { RightshipRuleParameters } from "./rightship-rule-parameters";
import { RightshipRuleHistoryTrail } from "./rightship-rule-history-trail";
import { CaSettings } from "../modules/customers/steps/ca-settings/ca-settings";
import { SettingsDocumentRequirements } from "@rightship-tech/document-management-app/views/pages/settings-document-requirements/settings-document-requirements";
import { CustomerDueDiligenceConfiguration } from "@rightship-tech/due-diligence-app/views/pages/customer-due-diligence-configuration/customer-due-diligence-configuration";
import { GoogleAnalyticsService } from "@rightship-tech/common-ui/services/analytics/google-analytics.service";

@autoinject()
export class Settings {

    private _tabs: StepModel[] = [];

    public viewModelInCompose: any = null;
    public model: any;
    public customer: CustomerModel;
    public initialized: boolean = false;
    ruleConfigParameters: LookupValue[] = [];
    
    @computedFrom(Reflection.getPropertyPath<Settings>(s => s._tabs))
    public get tabs(): StepModel[] {
        if (!this._tabs || !this._tabs.length) {
            this.updateTabs();
        }

        return this._tabs;
    }

    @observable
    public currentTab: StepModel = null;

    currentTabChanged(a, b) {
        if (a && a.title != (b ? b.title : '')) {
            this.currentTab ? this.currentTab.onClick(null) : null;
        }
    }

    constructor(private viewPeople: ViewPeople, private googleAnalyticsService: GoogleAnalyticsService,
        private zeroharmPartnerList: ZeroharmPartner,
        private i18N: I18N, private lookupValueService: LookupClientService,
        private userClaimsService: UserClaimsService,
        private userClientService: UserClientService,
        private customerService: CustomerService,
        private rightshipRuleParameters: RightshipRuleParameters,
        private customerRolesViewModel: CustomerRoles,
        private businessUnitViewModel: BusinessUnit,
        private rightshipRuleHistoryTrail: RightshipRuleHistoryTrail,
        private emissionsPerformanceTarget: CaSettings,
        private settingsDocumentRequirements: SettingsDocumentRequirements,
        private dueDiligenceConfiguration: CustomerDueDiligenceConfiguration) { }


    activate(args:any) {
        return new Promise<void>((resolve, reject) => {

            const request = new LookupValueRequest();
            request.lookupValueQuery = LookupValueQuery.RuleConfigParameters;
            const lookup = this.lookupValueService.getLookup(request);

            if (this.userClientService.currentUser.settings.customerId) {
                const getItemPromise = Promise.all([lookup, this.customerService.getCurrentCustomer()]);
                getItemPromise.then(x => {
                    this.ruleConfigParameters = x[0].activeValues;
                    this.customer = x[1].customers[0];
                    this.initialized = true;
                    this.updateTabs();

                    if (args && args.tab) {
                        this.currentTab = this.tabs.find(x =>(x.name && x.name === args.tab && x.show));
                        
                        if(this.currentTab) {
                            this.currentTab.onClick(null);
                        }
                    }   
  
                    resolve();
                }, reject);  
            }
            else {
                this.initialized = true;
                lookup.then(x => {
                    this.ruleConfigParameters = x.activeValues;
                    resolve();
                }, reject);
            } 
        });
    }

    private updateTabs() {
        const canEditRuleParamters = this.userClaimsService.hasPermission(Claims.CanEditRSRuleParameters);
        this._tabs = [
            {
                lazyLoad: true,
                show: () => this.customer && this.userClientService.currentUser.settings.customerId && 
                    (this.userClaimsService.hasPermission(Claims.CanViewAllBusinessUnits) ||
                    this.userClaimsService.hasPermission(Claims.CanViewMyBusinessUnit)),
                title: this.i18N.tr("business-unit-s-text"),
                step: 1,
                vm: this.businessUnitViewModel,
                onClick: () => {
                    this.model = {
                        id: this.userClientService.currentUser.settings.customerId,
                        customer: this.customer,
                        ruleConfigParameters: this.ruleConfigParameters
                    };
                }
            },
            {
                lazyLoad: true,
                show: () => this.customer && this.userClientService.currentUser.settings.customerId &&
                    this.userClaimsService.hasPermission(Claims.CanViewCustomerCarbonAccountingSettings),
                title: "Carbon Accounting",
                step: 2,
                vm: this.emissionsPerformanceTarget,
                onClick: () => {
                    this.model = {
                        customerId: this.userClientService.currentUser.settings.customerId,
                    };
                }
            },
            {
                lazyLoad: true,
                show: this.userClaimsService.hasPermission(Claims.CanManageRoles),
                title: this.i18N.tr("roles-text"),
                step: 3,
                vm: this.customerRolesViewModel,
                onClick: () => {
                    this.customerRolesViewModel.branchId = this.userClientService.currentUser.branchId
                    this.model = {};
                }
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("people-text"),
                step: 4,
                vm: this.viewPeople,
                show: this.userClaimsService.hasPermission(Claims.CanViewAllPeople) ||
                    this.userClaimsService.hasPermission(Claims.CanViewPeopleInMyBusinessUnit),
                onClick: () => {
                    this.model = {
                        branchId: this.userClientService.currentUser.branchId,
                        customerId: this.userClientService.currentUser.settings.customerId,
                        customers: this.customer ? [this.customer] : [],
                        canAddPerson: this.userClaimsService.hasPermission(Claims.CanAddPeopleRightShipUsers),
                        canEditPerson: this.userClaimsService.hasPermission(Claims.CanEditPeopleRightShipUsers)
                    };
                }
            },
            {
                lazyLoad: true,
                title: "Rightship rule parameters",
                step: 5,
                vm: this.rightshipRuleParameters,
                show: this.userClaimsService.hasPermission(Claims.RightShipUser) || canEditRuleParamters,
                onClick: () => {
                    this.model = {
                        isReadOnly: !canEditRuleParamters
                    };
                }
            },
            {
                lazyLoad: true,
				title: 'Zero Harm Innovation Partner List',
                step: 6,
                vm: this.zeroharmPartnerList,
                show: this.userClaimsService.hasPermission(Claims.CanViewZeroHarmParner),
                onClick: () => {
                    this.model = { };
                }
            },
            {
                lazyLoad: true,
                title: "History Trail",
                step: 7,
                vm: this.rightshipRuleHistoryTrail,
                show: this.userClaimsService.hasPermission(Claims.RightShipUser) || canEditRuleParamters,
                onClick: () => {}
            },
            {
                lazyLoad: true,
                title: `${this.userClaimsService.hasPermission(Claims.RightShipUser) ? "RightShip " : ""}Document Requirements`,
                step: 8,
                vm: this.settingsDocumentRequirements,
                show: this.userClaimsService.hasPermission(Claims.CanaccessDocumentRequirements),
                onClick: () => { 
                    
                    //Send event to google analytics
                    this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_document_requirements_tab", {});

                    //Model
                    this.model = { isRsUser: this.userClaimsService.hasPermission(Claims.RightShipUser) }; 
                }
            },
            {
                lazyLoad: true,
                title: "Due Diligence Configuration",
                name: "DueDiligenceConfiguration",
                step: 9,
                vm: this.dueDiligenceConfiguration,
                show: this.userClaimsService.hasPermission(Claims.CanAccessDueDiligenceConfigurationTab) 
                    && !this.userClaimsService.hasPermission(Claims.RightShipUser),
                onClick: () => {
                    this.model = {
                        customer: this.customer,
                        showSaveButton: true,
                        showNotifications: true
                    };
                }
            }
        ];
    }
}
